import { Send, Square, Plus, FileText, X, UploadCloud, PanelTop } from "lucide-react";
import { useRef, useEffect, useState } from "react";

export default function ChatInputBar({
    input, setInput, sendMessage,
    onStop,
    responseMode, setResponseMode, thinking,
    inputLocked = false,
    lockMessage = "กรุณาเลือกโมเดลก่อน",
    showStatusText = true,
    attachments = [],
    onPickFiles,
    onRemoveAttachment,
    onCanvasAction,
    activeTool = "",
    onClearTool,
    ocrStatusText = "",
    activeModelLabel = "",
    placeholderText = "",
}) {
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const plusMenuRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
    }, [input]);

    useEffect(() => {
        const onClickOutside = (event) => {
            if (!plusMenuRef.current) return;
            if (!plusMenuRef.current.contains(event.target)) {
                setIsPlusMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const handleDropFiles = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer?.files || []);
        if (files.length > 0) onPickFiles?.(files);
    };

    return (
        <div className="w-full relative z-30">
            <div
                onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragOver(true);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                        setIsDragOver(false);
                    }
                }}
                onDrop={handleDropFiles}
                className={`rainbow-hover-input relative backdrop-blur-xl border shadow-lg rounded-[28px] p-3 transition-all duration-300 focus-within:ring-2 ${
                    isDragOver
                        ? "bg-blue-50/90 border-blue-300 shadow-blue-500/20 focus-within:border-blue-300 focus-within:ring-blue-200"
                        : "bg-white/80 border-slate-200 shadow-blue-500/5 focus-within:shadow-blue-500/10 focus-within:border-blue-200 focus-within:ring-blue-100"
                }`}
            >
                {isDragOver && (
                    <div className="absolute inset-1 rounded-[22px] border-2 border-dashed border-blue-300 bg-blue-50/60 pointer-events-none flex items-center justify-center gap-2 text-blue-700 text-xs font-semibold">
                        <UploadCloud size={16} />
                        วางไฟล์ PDF/DOCX เพื่อแนบ
                    </div>
                )}
                {attachments.length > 0 && !thinking && (
                    <div className="mb-3 flex items-stretch gap-2 overflow-x-auto pb-1 pr-1">
                        {attachments.map((f) => (
                            (() => {
                                const ext = String(f.name || "").toLowerCase();
                                const isPdf = ext.endsWith(".pdf");
                                const isDocx = ext.endsWith(".docx");
                                const isExcel = ext.endsWith(".xlsx") || ext.endsWith(".xlsm") || ext.endsWith(".xls");
                                const iconClass = isPdf
                                    ? "mt-0.5 text-rose-500"
                                    : isDocx
                                        ? "mt-0.5 text-blue-500"
                                        : isExcel
                                            ? "mt-0.5 text-emerald-600"
                                            : "mt-0.5 text-slate-500";
                                const badgeClass = isPdf
                                    ? "bg-rose-50 text-rose-600 border-rose-200"
                                    : isDocx
                                        ? "bg-blue-50 text-blue-600 border-blue-200"
                                        : isExcel
                                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                            : "bg-slate-50 text-slate-600 border-slate-200";
                                const badgeText = isPdf ? "PDF" : isDocx ? "DOCX" : isExcel ? "EXCEL" : "FILE";
                                return (
                            <div
                                key={f.id}
                                className="group relative h-[90px] min-w-[145px] rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-2.5 shadow-sm"
                                title={f.name}
                            >
                                <button
                                    type="button"
                                    onClick={() => onRemoveAttachment?.(f.id)}
                                    className="absolute top-1.5 right-1.5 rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-red-500"
                                    aria-label="remove attachment"
                                >
                                    <X size={13} />
                                </button>
                                <div className="flex items-start gap-2">
                                    {f.state === "uploading" ? (
                                        <div className="relative mt-0.5 w-[18px] h-[18px] flex items-center justify-center">
                                            <svg className="w-[18px] h-[18px] -rotate-90">
                                                <circle
                                                    cx="9"
                                                    cy="9"
                                                    r="7"
                                                    strokeWidth="2"
                                                    className="stroke-slate-200 fill-none"
                                                />
                                                <circle
                                                    cx="9"
                                                    cy="9"
                                                    r="7"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    className="stroke-blue-500 fill-none transition-all duration-200"
                                                    style={{
                                                        strokeDasharray: `${2 * Math.PI * 7}`,
                                                        strokeDashoffset: `${2 * Math.PI * 7 * (1 - (Number(f.uploadProgress || 0) / 100))}`,
                                                    }}
                                                />
                                            </svg>
                                        </div>
                                    ) : (
                                        <FileText size={16} className={iconClass} />
                                    )}
                                    <div className="min-w-0">
                                        <div className="truncate text-xs font-semibold text-slate-700">{f.name}</div>
                                        <div className="mt-1 flex items-center gap-1.5">
                                            <span className={`inline-flex items-center rounded-md border px-1.5 py-[1px] text-[9px] font-bold tracking-wide ${badgeClass}`}>
                                                {badgeText}
                                            </span>
                                            <span className="text-[10px] text-slate-400">
                                            {f.state === "uploaded" && "แนบแล้ว"}
                                            {f.state === "uploading" && `กำลังอัปโหลด ${Math.round(Number(f.uploadProgress || 0))}%`}
                                            {f.state === "processing" && "กำลัง OCR..."}
                                            {f.state === "done" && "OCR เสร็จ"}
                                            {f.state === "error" && "ผิดพลาด"}
                                            {f.state === "selected" && "รออัปโหลด"}
                                            {f.state === "cancelled" && "ยกเลิกแล้ว"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                                );
                            })()
                        ))}
                    </div>
                )}
                <div className="relative flex items-end gap-2 rounded-2xl bg-white/70 px-2 py-1.5">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.xlsx,.xlsm,.xls,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) onPickFiles?.(files);
                        setIsPlusMenuOpen(false);
                        e.target.value = "";
                    }}
                />
                <div className="relative self-center flex-shrink-0" ref={plusMenuRef}>
                    <button
                        type="button"
                        onClick={() => {
                            if (thinking) return;
                            setIsPlusMenuOpen((prev) => !prev);
                        }}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100 transition"
                        title="ตัวเลือกเพิ่มเติม"
                        disabled={thinking}
                    >
                        <Plus size={18} />
                    </button>
                    {isPlusMenuOpen && !thinking && (
                        <div className="absolute left-0 bottom-11 z-40 min-w-[180px] rounded-xl border border-slate-200 bg-white shadow-lg p-1.5 space-y-1">
                            <button
                                type="button"
                                onClick={() => {
                                    fileInputRef.current?.click();
                                }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 transition text-left"
                            >
                                <FileText size={15} />
                                <span>Upload file</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    onCanvasAction?.("canvas");
                                    setIsPlusMenuOpen(false);
                                }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 transition text-left"
                            >
                                <PanelTop size={15} />
                                <span>Canvas</span>
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex-1 min-w-0">
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        placeholder={
                            inputLocked
                                ? "เลือกโมเดลก่อนเริ่มพิมพ์..."
                                : placeholderText
                                    ? placeholderText
                                    : activeTool === "canvas"
                                        ? "Let's write or build together"
                                        : "พิมพ์ข้อความของคุณ..."
                        }
                        className="w-full max-h-[120px] py-2 bg-transparent border-none outline-none text-slate-700 placeholder:text-slate-400 resize-none custom-scroll leading-relaxed"
                        value={input}
                        disabled={inputLocked}
                        onChange={(e) => {
                            if (inputLocked) return;
                            setInput(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (inputLocked) {
                                e.preventDefault();
                                return;
                            }
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                    />
                    {activeTool === "canvas" && (
                        <div className="mt-1.5">
                            <button
                                type="button"
                                onClick={() => onClearTool?.()}
                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2.5 h-8 text-xs font-medium text-slate-700 hover:bg-slate-200 transition"
                                title="ปิด Canvas mode"
                            >
                                <PanelTop size={13} />
                                <span>Canvas</span>
                                <X size={12} className="text-slate-500" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Send / Stop Button */}
                <div className="self-center flex-shrink-0 flex flex-col items-end gap-1">
                    {!!activeModelLabel && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 max-w-[132px] truncate">
                            {activeModelLabel}
                        </span>
                    )}
                    {thinking ? (
                        <button
                            onClick={onStop}
                            type="button"
                            className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm bg-red-500 text-white hover:bg-red-600 hover:shadow-red-300 hover:scale-105 active:scale-95"
                            title="หยุดการตอบ"
                            aria-label="หยุดการตอบ"
                        >
                            <Square size={16} className="fill-current" />
                        </button>
                    ) : (
                        <button
                            onClick={sendMessage}
                            disabled={inputLocked || (!input.trim() && attachments.length === 0)}
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm
                            ${(input.trim() || attachments.length > 0) && !inputLocked
                                ? "bg-gradient-to-tr from-blue-600 to-cyan-600 text-white hover:shadow-blue-300 hover:scale-105 active:scale-95"
                                : "bg-slate-100 text-slate-300 cursor-not-allowed"
                            }`}
                        >
                            <Send size={18} className="leading-none" />
                        </button>
                    )}
                </div>
                </div>
            </div>

            {/* Helper Text / Status */}
            {showStatusText && !thinking && inputLocked && (
                 <div className="absolute -top-8 left-4 text-xs font-medium text-slate-500 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                    {lockMessage}
                 </div>
            )}
            {showStatusText && thinking && (
                 <div className="absolute -top-8 left-4 text-xs font-medium text-blue-500 animate-pulse flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                    กำลังประมวลผล...
                 </div>
            )}
        </div>
    );
}
